import React, { useState, useEffect, use, useMemo } from 'react';
import { Plus, Search, Filter, Edit2, Trash2, Eye, Download, Upload, Users, Link2, Copy, ExternalLink, Share2, MessageCircle, Mail, Settings, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { memberAPI } from '../../../services/api';
import { showToast } from '../../../utils/toasts';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery';
import { useBranch } from '../../../context/BranchContext';
import { usePageTour } from '../../../hooks/usePageTour';
import DeleteMemberModal from '../../../components/member/DeleteMemberModal';
import FeatureGate from '../../../components/access/FeatureGate';
import ImportMembersModal from '../../../components/modals/ImportMembersModal';
import { useResourceLimit } from '../../../hooks/useResourceLimit';
import LimitReachedModal from '../../../components/modals/LimitReachedModal';
import RegistrationSettingsPanel from '../../../components/modals/RegistrationSettingsPanel';
import { useAuth } from '../../../context/AuthContext';
import PermissionGuard from '../../../components/guards/PermissionGuard';
import Loader from '../../../components/ui/Loader';

const AllMembers = () => {
  const { user, fetchAndUpdateSubscription } = useAuth();
  const { selectedBranch } = useBranch();
  usePageTour('members_all');
  const plan = user?.merchant?.subscription?.plan;
  const merchantId = user?.merchant?.id;
  const merchantName = user?.merchant?.name || 'Church';
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState('all'); // 'all', 'today', 'yesterday', 'custom'
  const [exportCustomStartDate, setExportCustomStartDate] = useState('');
  const [exportCustomEndDate, setExportCustomEndDate] = useState('');
  const [exportMembershipType, setExportMembershipType] = useState('all'); // 'all', 'member', 'visitor', 'leader'
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPublicLinks, setShowPublicLinks] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    visitors: 0,
    male: 0,
    female: 0,
    leaders: { pastors: 0, elders: 0, deacons: 0, leaders: 0, total: 0 },
    members: 0,
    firstTimersCount: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Filters
  const [filters, setFilterState] = useState({
    status: '',
    gender: '',
    membershipType: '',
    registrationType: '',
  });

  // Members fetcher for SWR
  const membersFetcher = async (params: any) => {
    const response = await memberAPI.getMembers(params);
    return {
      items: response.data.data.members,
      pagination: {
        currentPage: response.data.data.pagination?.currentPage || 1,
        pages: response.data.data.pagination?.totalPages || 1,
        totalItems: response.data.data.pagination?.totalItems || response.data.data.members.length,
      },
    };
  };

  const {
    data: members,
    loading,
    currentPage,
    totalPages,
    totalItems: totalMembers,
    setPage,
    setSearch,
    setFilters,
    refetch: refetchMembers
  } = usePaginatedQuery<any>(`members-${selectedBranch?._id || 'all'}`, membersFetcher, {
    limit: 20
  });

  // Build and apply filters when activeTab or filters change
  useEffect(() => {
    const filterObj: any = {};
    if (activeTab !== 'all') {
      filterObj.membershipType = activeTab;
    }
    if (filters.status) filterObj.status = filters.status;
    if (filters.gender) filterObj.gender = filters.gender;
    if (filters.membershipType && activeTab === 'all') {
      filterObj.membershipType = filters.membershipType;
    }
    if (filters.registrationType && activeTab === 'all') {
      filterObj.registrationType = filters.registrationType;
    }
    setFilters(filterObj);
  }, [activeTab, filters, setFilters]);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Resource Limits
  const [showLimitModal, setShowLimitModal] = useState(false);
  const memberLimit = useResourceLimit('members');

  const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const registrationLink = `${frontendUrl}/register/${merchantId}`;

  useEffect(() => {
    fetchStats();
  }, [selectedBranch?._id]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await memberAPI.getStats();
      const data = response.data.data?.stats;

      const statusMap = data.byStatus.reduce((acc: any, item: any) => {
        acc[item._id] = item.count;
        return acc;
      }, {});

      const genderMap = data.byGender.reduce((acc: any, item: any) => {
        acc[item._id] = item.count;
        return acc;
      }, {});

      setStats({
        total: data.total,
        active: statusMap.active || 0,
        inactive: statusMap.inactive || 0,
        visitors: statusMap.visitor || 0,
        male: genderMap.male || 0,
        female: genderMap.female || 0,
        leaders: data?.leaders,
        members: data?.regularMembers || 0,
        firstTimersCount: data?.firstTimersCount || 0,
      });
    } catch (error) {
      console.error('Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAddMemberClick = () => {
    if (!memberLimit.canCreate) {
      setShowLimitModal(true);
      return;
    }
    navigate('/members/new');
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const executeExport = async () => {
    try {
      setIsExporting(true);

      const params: any = {};

      // Add date range params
      if (exportDateRange === 'today') {
        const today = new Date();
        params.startDate = today.toISOString().split('T')[0];
        params.endDate = today.toISOString().split('T')[0];
      } else if (exportDateRange === 'yesterday') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        params.startDate = yesterday.toISOString().split('T')[0];
        params.endDate = yesterday.toISOString().split('T')[0];
      } else if (exportDateRange === 'custom') {
        if (!exportCustomStartDate || !exportCustomEndDate) {
          showToast.error('Please select both start and end dates');
          setIsExporting(false);
          return;
        }
        params.startDate = exportCustomStartDate;
        params.endDate = exportCustomEndDate;
      }

      // Add membership type filter
      if (exportMembershipType !== 'all') {
        params.membershipStatus = exportMembershipType;
      }

      const response = await memberAPI.exportMembers(params);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateLabel = exportDateRange === 'all' ? 'all' : exportDateRange === 'today' ? 'today' : exportDateRange === 'yesterday' ? 'yesterday' : `${exportCustomStartDate}_to_${exportCustomEndDate}`;
      const typeLabel = exportMembershipType !== 'all' ? `_${exportMembershipType}` : '';
      link.setAttribute('download', `members_${dateLabel}${typeLabel}_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Members exported successfully!');
      setShowExportModal(false);
      // Reset state
      setExportDateRange('all');
      setExportCustomStartDate('');
      setExportCustomEndDate('');
      setExportMembershipType('all');
    } catch (error: any) {
      showToast.error(error.response?.data?.message || 'Failed to export members');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportComplete = () => {
    refetchMembers();
    fetchStats();
  };

  const handleDelete = (member: any) => {
    setSelectedMember({
      id: member._id,
      name: member.fullName || `${member.firstName} ${member.lastName}`
    });
    setShowDeleteModal(true);
  };

  const toggleMemberSelection = (memberId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set());
    } else {
      const allIds = new Set(members.map(m => m._id));
      setSelectedMembers(allIds);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMembers.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async (permanent: boolean) => {
    setIsDeleting(true);
    try {
      const memberIds = Array.from(selectedMembers);

      // Delete members one by one
      for (const memberId of memberIds) {
        await memberAPI.deleteMember(memberId, permanent);
      }

      showToast.success(`${selectedMembers.size} member(s) deleted successfully`);
      setSelectedMembers(new Set());
      setShowBulkDeleteModal(false);
      await fetchAndUpdateSubscription();
      await refetchMembers();
      await fetchStats();
    } catch (error: any) {
      showToast.error('Failed to delete some members');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSuccess = async (permanent: boolean = false) => {
    await memberAPI.deleteMember(selectedMember.id, permanent);
    showToast.success('Member deleted successfully');
    setShowDeleteModal(false);
    setSelectedMember(null);
    await fetchAndUpdateSubscription();
    await refetchMembers();
    await fetchStats();
  };

  const copyRegistrationLink = () => {
    navigator.clipboard.writeText(registrationLink);
    showToast.success('Registration link copied to clipboard!');
  };

  // ⭐ NEW: Share via WhatsApp
  const shareViaWhatsApp = () => {
    const message = `Join ${merchantName}! Register here: ${registrationLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // ⭐ NEW: Share via SMS
  const shareViaSMS = () => {
    const message = `Join ${merchantName}! Register here: ${registrationLink}`;
    const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  };

  // ⭐ NEW: Share via Email
  const shareViaEmail = () => {
    const subject = `Join ${merchantName}`;
    const body = `You're invited to register as a member of ${merchantName}!\n\nClick here to register: ${registrationLink}\n\nYou can choose to register as a member or mark yourself as a first-time visitor.`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  // ⭐ NEW: Native Share API (for mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${merchantName}`,
          text: `Register as a member of ${merchantName}`,
          url: registrationLink,
        });
        showToast.success('Link shared successfully!');
      } catch (error: any) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    } else {
      // Fallback: just copy to clipboard
      copyRegistrationLink();
    }
  };

  const tabs = [
    { id: 'all', label: 'All', count: stats?.total },
    { id: 'leader', label: 'Leaders', count: stats?.leaders?.leaders || 0 },
    { id: 'pastor', label: 'Pastors', count: stats?.leaders?.pastors || 0 },
    { id: 'member', label: 'Members', count: stats?.members || 0 },
    { id: 'first-timer', label: 'First Timers', count: stats?.firstTimersCount || 0 },
  ];

  const handleTabClick = (tab: any) => {
    setActiveTab(tab.id);
    setPage(1);
    setFilterState(prev => ({
      ...prev,
      registrationType: '',
      membershipType: ''
    }));
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: any = {
      pastor: 'bg-purple-100 dark:bg-purple-900/20 text-primary-700 dark:text-primary-300',
      leader: 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-blue-300',
      elder: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
      deacon: 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
      member: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      youth: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
      children: 'bg-pink-100 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',
      visitor: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',

    };
    return colors[role] || colors.visitor;
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: any = {
      active: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
      inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      visitor: 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-blue-300',
      first_timer: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
      new_convert: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
      transferred: 'bg-purple-100 dark:bg-purple-900/20 text-primary-700 dark:text-primary-300',
    };
    return colors[status] || colors.active;
  };

  return (
    <FeatureGate feature={'memberManagement'}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Member Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your church members and their information
            </p>
          </div>
          <div className="hidden md:flex items-center space-x-3">

            {/* Export Button */}
            <PermissionGuard permission="members.export">
              <button
                onClick={handleExport}
                disabled={isExporting || members.length === 0}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            </PermissionGuard>

            {/* Import Button */}
            <PermissionGuard permission="members.import">
              <button
                onClick={() => setShowImportModal(true)}
                data-tour="members-import"
                className="flex items-center px-4 py-2 text-sm font-medium text-primary-700 dark:text-blue-300 bg-primary-50 dark:bg-primary-900/20 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-primary-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </button>
            </PermissionGuard>

            {/* Show/Hide Public Links Button */}
            <PermissionGuard permission="members.registrationLink">
              <button
                onClick={() => setShowPublicLinks(!showPublicLinks)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                title={showPublicLinks ? 'Hide public links' : 'Show public links'}
              >
                <Settings className="w-4 h-4 mr-2" />
                {showPublicLinks ? 'Hide' : 'Show'} Links
              </button>
            </PermissionGuard>

            {/* Add Member Button */}
            <PermissionGuard permission="members.create">
              <button
                onClick={handleAddMemberClick}
                data-tour="members-add-btn"
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${memberLimit.canCreate
                  ? 'text-white bg-primary-600 hover:bg-primary-700'
                  : 'text-gray-400 bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                  }`}
                title={!memberLimit.canCreate ? `Member limit reached (${memberLimit.current}/${memberLimit.limit})` : ''}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Member
                {memberLimit.isNearLimit && memberLimit.canCreate && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                    {memberLimit.remaining} left
                  </span>
                )}
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Member Usage Card - Progressive status with visual feedback */}
        {showPublicLinks && (
          <div className={`rounded-xl p-6 border-[1px] transition-all ${!memberLimit.canCreate
            ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-800'
            : memberLimit.isNearLimit
              ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-300 dark:border-orange-800'
              : 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-primary-200 dark:border-primary-800'
            }`}>
            {/* Top Section: Registration Link and Usage Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Public Registration */}
              <div>
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Link2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Public Registration
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Share this link for members to self-register online. They can choose to register as members or mark themselves as first-time visitors.
                    </p>
                    <div className="flex items-start gap-3 mt-4">
                      <PermissionGuard permission="members.registrationLink">
                        <button
                          onClick={() => setShowLinkModal(true)}
                          className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center space-x-2"
                        >
                          <Share2 className="h-4 w-4" />
                          <span>Get Link</span>
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="members.registrationSettings">
                        <button
                          onClick={() => setShowSettingsModal(true)}
                          className="px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center space-x-2"
                          title="Registration Settings"
                        >
                          <Settings className="h-4 w-4" />
                          <span>Settings</span>
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Member Usage */}
              <div className="lg:border-l lg:border-gray-300 dark:lg:border-gray-600 lg:pl-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`p-3 rounded-lg ${!memberLimit.canCreate
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : memberLimit.isNearLimit
                      ? 'bg-orange-100 dark:bg-orange-900/30'
                      : 'bg-primary-100 dark:bg-primary-900/30'
                    }`}>
                    <Users className={`h-6 w-6 ${!memberLimit.canCreate
                      ? 'text-red-600 dark:text-red-400'
                      : memberLimit.isNearLimit
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-primary-600 dark:text-primary-400'
                      }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Member Usage
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {memberLimit.limit === null || memberLimit.limit === undefined ? 'Unlimited' : `${memberLimit.current} of ${memberLimit.limit} members used`}
                    </p>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {memberLimit.current}
                      {(memberLimit.limit !== null && memberLimit.limit !== undefined) && (
                        <span className="text-lg text-gray-500 dark:text-gray-400">/{memberLimit.limit}</span>
                      )}
                    </div>
                    {(memberLimit.limit !== null && memberLimit.limit !== undefined) && (
                      <div className="text-sm mt-1">
                        {!memberLimit.canCreate ? (
                          <span className="text-red-600 dark:text-red-400 font-semibold">
                            Limit Reached
                          </span>
                        ) : memberLimit.isNearLimit ? (
                          <span className="text-orange-600 dark:text-orange-400 font-semibold">
                            {memberLimit.remaining} remaining
                          </span>
                        ) : (
                          <span className="text-primary-600 dark:text-primary-400 font-semibold">
                            {memberLimit.remaining} remaining
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {(memberLimit.limit !== null && memberLimit.limit !== undefined) && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${!memberLimit.canCreate
                          ? 'bg-gradient-to-r from-red-500 to-red-600'
                          : memberLimit.isNearLimit
                            ? 'bg-gradient-to-r from-orange-400 to-orange-500'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                          }`}
                        style={{ width: `${Math.min((memberLimit.current / memberLimit.limit) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        {((memberLimit.current / memberLimit.limit) * 100).toFixed(1)}% used
                      </span>
                      {!memberLimit.canCreate && (
                        <button
                          onClick={() => navigate('/settings?tab=billing')}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-semibold flex items-center space-x-1"
                        >
                          <span>Upgrade Plan</span>
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Toolbar: Export / Import / Show Links */}
        <div className="md:hidden flex items-center gap-2">
          <PermissionGuard permission="members.export">
            <button
              onClick={handleExport}
              disabled={isExporting || members.length === 0}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </PermissionGuard>

          <PermissionGuard permission="members.import">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-700 dark:text-blue-300 bg-primary-50 dark:bg-primary-900/20 border border-blue-300 dark:border-blue-700 rounded-lg"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
          </PermissionGuard>

          <PermissionGuard permission="members.registrationLink">
            <button
              onClick={() => setShowPublicLinks(!showPublicLinks)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
              title={showPublicLinks ? 'Hide public links' : 'Show public links'}
            >
              <Settings className="w-4 h-4" />
              {showPublicLinks ? 'Hide' : 'Show'} Links
            </button>
          </PermissionGuard>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {statsLoading ? (
            <>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 mb-3"></div>
                      <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                    </div>
                    <div className="p-3 bg-gray-300 dark:bg-gray-600 rounded-lg w-12 h-12"></div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Members</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{stats.total}</p>
                  </div>
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.active}</p>
                  </div>
                  <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Male</p>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-1">{stats.male}</p>
                  </div>
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Female</p>
                    <p className="text-2xl font-bold text-pink-600 dark:text-pink-400 mt-1">{stats.female}</p>
                  </div>
                  <div className="p-3 bg-pink-100 dark:bg-pink-900/20 rounded-lg">
                    <svg className="w-6 h-6 text-pink-600 dark:text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Tabs and Search */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Tabs */}
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === tab.id
                      ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                  >
                    {tab.label}
                    {tab.count !== undefined && (
                      <span className="ml-2 text-xs">({tab.count})</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative flex items-center space-x-3 w-full sm:w-auto" data-tour="members-search">
                <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearch(e.target.value);
                  }}
                  className="flex-1 min-w-0 sm:flex-none sm:w-auto pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                {selectedMembers.size > 0 && (
                  <PermissionGuard permission="members.delete">
                    <button
                      onClick={handleBulkDelete}
                      disabled={isDeleting}
                      className="flex-shrink-0 flex items-center gap-1.5 px-2.5 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:hover:bg-red-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="hidden sm:inline">Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete </span>
                          {selectedMembers.size}
                        </>
                      )}
                    </button>
                  </PermissionGuard>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <Loader variant="skeleton-table" count={8} />
          ) : members.length === 0 ? (
            <div className="text-center py-12 flex flex-col items-center justify-center">
              <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2 dark:text-gray-400">No members found</p>
              {/* Add Member Button */}
              <PermissionGuard permission="members.create">
                <button
                  onClick={handleAddMemberClick}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${memberLimit.canCreate
                    ? 'text-white bg-primary-600 hover:bg-primary-700'
                    : 'text-gray-400 bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                    }`}
                  title={!memberLimit.canCreate ? `Member limit reached (${memberLimit.current}/${memberLimit.limit})` : ''}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Member
                </button>
              </PermissionGuard>
            </div>
          ) : (
            <>
              {/* Mobile Card List */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {members.map((member) => (
                  <div
                    key={member._id}
                    onClick={() => navigate(`/members/${member._id}`)}
                    className={`p-4 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer ${selectedMembers.has(member._id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center cursor-pointer group flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={selectedMembers.has(member._id)}
                            onChange={() => { toggleMemberSelection(member._id) }}
                            onClick={(e) => e.stopPropagation()}
                            className="sr-only peer"
                          />
                          <div className="w-5 h-5 rounded-md border-2 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 peer-checked:bg-primary-600 peer-checked:border-primary-600 dark:peer-checked:bg-primary-600 dark:peer-checked:border-primary-600 transition-all group-hover:border-primary-400 dark:group-hover:border-primary-500"></div>
                          <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white hidden peer-checked:block pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      </label>

                      {member.photo ? (
                        <img
                          src={member.photo}
                          alt={member.fullName || `${member.firstName} ${member.lastName}`}
                          className="flex-shrink-0 h-11 w-11 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                        />
                      ) : (
                        <div className="flex-shrink-0 h-11 w-11 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 dark:text-primary-400 font-semibold text-sm">
                            {member.firstName[0]}{member.lastName[0]}
                          </span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {member.fullName || `${member.firstName} ${member.lastName}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{member.phone || 'N/A'}</p>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <PermissionGuard permission="members.edit">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/members/${member._id}/edit`);
                            }}
                            className="p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard permission="members.delete">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(member);
                            }}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </PermissionGuard>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                        <label className="inline-flex items-center cursor-pointer group">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={members.length > 0 && selectedMembers.size === members.length}
                              onChange={toggleSelectAll}
                              className="sr-only peer"
                              title="Select all members"
                            />
                            <div className="w-5 h-5 rounded-md border-2 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 peer-checked:bg-primary-600 peer-checked:border-primary-600 dark:peer-checked:bg-primary-600 dark:peer-checked:border-primary-600 transition-all peer-focus:ring-1 peer-focus:ring-primary-500 peer-focus:ring-offset-2 dark:peer-focus:ring-offset-gray-900 peer-focus:ring-offset-0 dark:peer-focus:ring-offset-0 group-hover:border-primary-400 dark:group-hover:border-primary-500"></div>
                            <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white hidden peer-checked:block pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                        </label>
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                        Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                        Branch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                        Role
                      </th>

                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {members.map((member) => (
                      <tr key={member._id} onClick={(e) => { e?.stopPropagation(); navigate(`/members/${member._id}`) }} className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group ${selectedMembers.has(member._id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        }`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <label className="inline-flex items-center cursor-pointer group" onClick={(e) => e.stopPropagation()}>
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={selectedMembers.has(member._id)}
                                onChange={() => { toggleMemberSelection(member._id) }}
                                onClick={(e) => e.stopPropagation()}
                                className="sr-only peer"
                              />
                              <div className="w-5 h-5 rounded-md border-2 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 peer-checked:bg-primary-600 peer-checked:border-primary-600 dark:peer-checked:bg-primary-600 dark:peer-checked:border-primary-600 transition-all peer-focus:ring-1 peer-focus:ring-primary-500 peer-focus:ring-offset-2 dark:peer-focus:ring-offset-gray-900 focus:ring-offset-0 dark:peer-focus:ring-offset-0 group-hover:border-primary-400 dark:group-hover:border-primary-500"></div>
                              <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white hidden peer-checked:block pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path>
                              </svg>
                            </div>
                          </label>
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {member.photo ? (
                              <img
                                src={member.photo}
                                alt={member.fullName || `${member.firstName} ${member.lastName}`}
                                className="flex-shrink-0 h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                              />
                            ) : (
                              <div className="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                                <span className="text-primary-600 dark:text-primary-400 font-semibold text-sm">
                                  {member.firstName[0]}{member.lastName[0]}
                                </span>
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {member.fullName || `${member.firstName} ${member.lastName}`}
                              </div>
                              <div className="text-xs capitalize text-gray-500 dark:text-gray-400">
                                {member.gender}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">{member.email || 'N/A'}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{member.phone || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {member.branch?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.membershipType)}`}>
                            {member.membershipType?.charAt(0).toUpperCase() + member.membershipType?.slice(1)}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <PermissionGuard permission="members.edit">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/members/${member._id}/edit`);
                                }}
                                className="p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </PermissionGuard>
                            <PermissionGuard permission="members.delete">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(member);
                                }}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </PermissionGuard>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {/* Selection Info Bar */}
              {selectedMembers.size > 0 && (
                <div className="px-6 py-3 bg-primary-50 dark:bg-primary-900/20 border-t border-primary-200 dark:border-blue-700 flex items-center justify-between">
                  <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => setSelectedMembers(new Set())}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Clear selection
                  </button>
                </div>
              )}

              {totalPages > 1 && (
                <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-0 sm:justify-between">
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Showing {(currentPage - 1) * 20 + 1} to {Math.min(currentPage * 20, totalMembers)} of {totalMembers} members
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => currentPage > 1 && setPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => currentPage < totalPages && setPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ⭐ UPDATED: Registration Link Modal with Share Options */}
        {showLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Share Registration Link
                </h3>
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Share this link with members so they can register themselves. They can choose between member registration or first-timer forms.
              </p>

              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
                <code className="text-sm text-gray-800 dark:text-gray-200 break-all">
                  {registrationLink}
                </code>
              </div>

              {/* ⭐ NEW: Action Buttons Grid */}
              <div className="space-y-3">
                {/* Copy and Open buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={copyRegistrationLink}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Copy className="h-4 w-4" />
                    <span>Copy Link</span>
                  </button>

                  <button
                    onClick={() => window.open(registrationLink, '_blank')}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Open</span>
                  </button>
                </div>

                {/* Share options divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      Share via
                    </span>
                  </div>
                </div>

                {/* ⭐ NEW: Share via buttons */}
                <div className="grid grid-cols-2 gap-3">
                  {/* WhatsApp */}
                  <button
                    onClick={shareViaWhatsApp}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>WhatsApp</span>
                  </button>

                  {/* SMS */}
                  <button
                    onClick={shareViaSMS}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>SMS</span>
                  </button>

                  {/* Email */}
                  <button
                    onClick={shareViaEmail}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </button>

                  {/* Native Share (if supported) */}
                  <button
                    onClick={handleNativeShare}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Share2 className="h-4 w-4" />
                    <span>More</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <DeleteMemberModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedMember(null);
          }}
          onDelete={handleSuccess}
          memberName={selectedMember?.name}
          isLoading={isDeleting}
        />

        {/* Bulk Delete Modal */}
        <DeleteMemberModal
          isOpen={showBulkDeleteModal}
          onClose={() => {
            setShowBulkDeleteModal(false);
          }}
          onDelete={confirmBulkDelete}
          memberName={`${selectedMembers.size} member${selectedMembers.size !== 1 ? 's' : ''}`}
          isLoading={isDeleting}
        />

        {/* Mobile: Floating Add Member Button */}
        <PermissionGuard permission="members.create">
          <button
            onClick={handleAddMemberClick}
            className="md:hidden fixed right-4 z-30 w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-colors"
            style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
            title="Add Member"
          >
            <Plus className="w-6 h-6" />
          </button>
        </PermissionGuard>

        <ImportMembersModal
          isOpen={showImportModal}
          onClose={() => { fetchAndUpdateSubscription(); setShowImportModal(false) }}
          onImportComplete={handleImportComplete}
        />

        <LimitReachedModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          resourceType="members"
          planName={plan}
          current={memberLimit.current}
          limit={memberLimit.limit || 0}
        />

        {/* Registration Settings Modal */}
        <RegistrationSettingsPanel
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Export Members
                </h3>

                <div className="space-y-4">
                  {/* Membership Type Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Membership Type
                    </label>
                    <select
                      value={exportMembershipType}
                      onChange={(e) => setExportMembershipType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="all">All Members</option>
                      <option value="member">Members Only</option>
                      <option value="visitor">Visitors Only</option>
                      <option value="leader">Leaders Only</option>
                    </select>
                  </div>

                  {/* Date Range Options */}
                  <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Join Date Range
                    </label>

                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                        <input
                          type="radio"
                          name="exportDateRange"
                          value="all"
                          checked={exportDateRange === 'all'}
                          onChange={(e) => setExportDateRange(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">All Time</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                        <input
                          type="radio"
                          name="exportDateRange"
                          value="today"
                          checked={exportDateRange === 'today'}
                          onChange={(e) => setExportDateRange(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Today</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                        <input
                          type="radio"
                          name="exportDateRange"
                          value="yesterday"
                          checked={exportDateRange === 'yesterday'}
                          onChange={(e) => setExportDateRange(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Yesterday</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                        <input
                          type="radio"
                          name="exportDateRange"
                          value="custom"
                          checked={exportDateRange === 'custom'}
                          onChange={(e) => setExportDateRange(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Custom Range</span>
                      </label>
                    </div>
                  </div>

                  {/* Custom Date Range Inputs */}
                  {exportDateRange === 'custom' && (
                    <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={exportCustomStartDate}
                          onChange={(e) => setExportCustomStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          End Date {!exportCustomStartDate && <span className="text-gray-500 text-xs">(Select start date first)</span>}
                        </label>
                        <input
                          type="date"
                          value={exportCustomEndDate}
                          onChange={(e) => setExportCustomEndDate(e.target.value)}
                          disabled={!exportCustomStartDate}
                          min={exportCustomStartDate}
                          max={exportCustomStartDate ? new Date(new Date(exportCustomStartDate).setMonth(new Date(exportCustomStartDate).getMonth() + 3)).toISOString().split('T')[0] : ''}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
                        />
                        {exportCustomStartDate && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Max: {new Date(new Date(exportCustomStartDate).setMonth(new Date(exportCustomStartDate).getMonth() + 3)).toISOString().split('T')[0]}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Actions */}
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => {
                      setShowExportModal(false);
                      setExportDateRange('all');
                      setExportCustomStartDate('');
                      setExportCustomEndDate('');
                      setExportMembershipType('all');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeExport}
                    disabled={isExporting || (exportDateRange === 'custom' && (!exportCustomStartDate || !exportCustomEndDate))}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isExporting ? (
                      <>
                        <Loader variant="inline" size="sm" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Export
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </FeatureGate>
  );
};

export default AllMembers;